import { useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { FORM_MODES } from 'config/formModes';
import { useEntityType } from 'hooks';
import { ENTITY, PATH_TO_ENTITY_MAP } from 'modules/campaigns/config';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { APP_EVENT_TYPE, useAppEventContext } from 'modules/taboola-common-frontend-modules/app-events-aggregator';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { useBudgetRecommendation } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useBudgetRecommendation';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { useMultiLayerPathParams } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerPathParams';

/**
 * Custom hook to trigger budget advisor recommendation initialization
 */
export const useBudgetAdvisorRecommendationTrigger = () => {
    const { push: pushAppEvent } = useAppEventContext();
    const { data: formData, mode } = useFormDataContext();
    const entityType = useEntityType(PATH_TO_ENTITY_MAP);
    const { entity } = useMultiLayerPathParams();
    const { remove: removeRecommendation } = useBudgetRecommendation();
    const isBudgetRecommendationPermitted = usePermissions('CHAT_AGENT_BUDGET_RECOMMENDATION');
    const isRecommendationEnabled = useConfigMatch({
        [FEATURE_FLAGS.CHAT_AGENT_BUDGET_RECOMMENDATION_ENABLED]: 'true',
    });

    const isPermitted =
        isBudgetRecommendationPermitted &&
        (mode === FORM_MODES.CREATE || mode === FORM_MODES.DUPLICATE) &&
        isRecommendationEnabled &&
        entityType === ENTITY.CAMPAIGN;

    const pushAppFormData = useMemo(() => {
        if (isEmpty(formData) || !entity) {
            return undefined;
        }

        return { [entity]: formData };
    }, [entity, formData]);

    useEffect(() => {
        if (isPermitted) {
            pushAppEvent({
                id: 'CAMPAIGN_BUDGET_NEGOTIATION_RECOMMENDATION',
                triggerName: 'budget_negotiation_recommendation',
                type: APP_EVENT_TYPE.VISIBLE,
                data: pushAppFormData,
            });
        }

        return () => {
            removeRecommendation();
        };
        // eslint-disable-next-line
    }, [isPermitted, pushAppEvent, removeRecommendation]);
};
